import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  startOfMonth,
  endOfMonth,
  addDays,
  format,
  isSameDay,
  addMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  differenceInCalendarDays,
  sub,
} from "date-fns";
import { FaChevronLeft, FaChevronRight, FaHome, FaUsers } from "react-icons/fa";
import ManagerNavbar from "../Layout/ManagerNavbar";
import axios from "axios";
import API_BASE_URL from "../config";

const ManagerTimesheetGrid = () => {
  const { managerId } = useParams();
  const navigate = useNavigate();
  const invalidDateToastShown = useRef(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [worklogs, setWorklogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [viewMode, setViewMode] = useState("monthly");
  const [billableFilter, setBillableFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
  const [error, setError] = useState("");
  const [holidayDates, setHolidayDates] = useState([]);
  const [holidayMap, setHolidayMap] = useState({});
  const [weeklyHolidays, setWeeklyHolidays] = useState([]);
  const [leaveDates, setLeaveDates] = useState([]);

  const token = sessionStorage.getItem("token");
  const employeeId = sessionStorage.getItem("employeeId"); // acts as reporter/manager id


  useEffect(() => {
  if (!token || !employeeId) {
    console.error("Missing token or employeeId");
    navigate("/");
    return;
  }

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/manageteam/reporter/${employeeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const raw = res.data;
      const arr = Array.isArray(raw) ? raw : (raw ? [raw] : []);

      // ✅ Extract employee objects
      const extractedEmployees = arr
        .map((item) => item?.employee || null)
        .filter(Boolean);

      // ✅ Remove duplicates by employee.id
      const seenIds = new Set();
      const uniqueEmployees = extractedEmployees.filter((emp) => {
        if (!emp.id) return false; // skip invalid
        if (seenIds.has(emp.id)) return false;
        seenIds.add(emp.id);
        return true;
      });

      console.log("Extracted employees:", extractedEmployees);
      console.log("Unique employees:", uniqueEmployees);

      if (uniqueEmployees.length === 0) {
        setEmployees([]);
        setSelectedEmployee(null);
        setError("No team members found.");
        return;
      }

      setEmployees(uniqueEmployees);

      // ✔ Match old behavior: select FIRST employee
      setSelectedEmployee(uniqueEmployees[0]);

    } catch (err) {
      console.error("Failed to fetch employees (manageteam):", err);
      setError("Unable to load your team members.");
    }
  };

  fetchEmployees();
}, [token, employeeId, navigate, API_BASE_URL]);


  // Load Bootstrap dynamically
  useEffect(() => {
    // Bootstrap CSS
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    bootstrapLink.id = "timesheetgrid-bootstrap";
    document.head.appendChild(bootstrapLink);

    // Bootstrap Icons CSS
    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    iconsLink.id = "timesheetgrid-icons";
    document.head.appendChild(iconsLink);

    return () => {
      const existingBootstrap = document.getElementById("timesheetgrid-bootstrap");
      if (existingBootstrap) existingBootstrap.remove();

      const existingIcons = document.getElementById("timesheetgrid-icons");
      if (existingIcons) existingIcons.remove();
    };
  }, []);


  useEffect(() => {
    fetchWeeklyHolidays();
  }, []);

  async function fetchWeeklyHolidays() {
    try {
      const res = await axios.get("/api/holidays/weekly");
      const days = res.data.map(h => h.name);
      setWeeklyHolidays(days);
    } catch (err) {
      console.error("Failed to fetch weekly holidays:", err);
    }
  }

  const isWeeklyHoliday = (day) => {
    const dayName = format(day, "EEEE"); // e.g. "Sunday"
    return weeklyHolidays.includes(dayName);
  };

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/holidays`);
        const data = await res.json();

        const allHolidayDates = [];
        const holidayNameMap = {};

        data.forEach(h => {
          const start = new Date(h.startDate);
          const end = new Date(h.endDate);

          // Go through each date between start and end
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const formatted = d.toISOString().split("T")[0];
            allHolidayDates.push(formatted);
            holidayNameMap[formatted] = h.name;
          }
        });

        setHolidayDates(allHolidayDates);
        setHolidayMap(holidayNameMap);
        console.log("Fetched holidays:", allHolidayDates);
      } catch (err) {
        console.error("Error fetching holidays:", err);
      }
    };

    fetchHolidays();
  }, []);


  const fetchLeaves = async () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      console.warn("No employee selected — skipping fetchLeaves");
      setLeaveDates([]);
      return;
    }

    const token = sessionStorage.getItem("token");

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/leaves/employee/${selectedEmployee.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const leaveList = [];
      res.data.forEach((lv) => {
        const start = new Date(lv.startDate);
        const end = new Date(lv.endDate);

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          leaveList.push(d.toISOString().split("T")[0]);
        }
      });

      setLeaveDates(leaveList);
    } catch (err) {
      console.error("Error fetching leave:", err);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, [selectedEmployee]);

  const isLeaveDay = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return leaveDates.includes(formatted);
  };


  

  // Load worklogs and tasks when employee changes
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!selectedEmployee || !token) return;

    const fetchWorklogsAndTasks = async () => {
      setLoading(true);
      try {
        const logsRes = await axios.get(
          `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWorklogs(Array.isArray(logsRes.data) ? logsRes.data : []);

        const tasksRes = await axios.get(
          `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}/tasks`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
      } catch (err) {
        console.error("Error fetching worklogs:", err);
        setError("Unable to fetch timesheet data.");
      } finally {
        setLoading(false);
      }
    };

    fetchWorklogsAndTasks();
  }, [selectedEmployee]);


  const isHolidayColumn = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return holidayDates.includes(formatted);
  };


  const handleExport = async (fileFormat) => {
    if (!selectedEmployee) return;
    const token = sessionStorage.getItem("token");
    if (!token) return;

    try {
      // Format dates as yyyy-MM-dd
      const fromDateStr = startDate ? startDate.toISOString().split("T")[0] : "";
      const toDateStr = endDate ? endDate.toISOString().split("T")[0] : "";

      // Build URL params
      const params = new URLSearchParams();
      params.append("type", "date"); // must be "date" for fromDate/toDate filter
      if (fromDateStr) params.append("fromDate", fromDateStr);
      if (toDateStr) params.append("toDate", toDateStr);

      // Add billing type separately as a query param if needed
      if (billableFilter && billableFilter !== "all") params.append("billingType", billableFilter);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/timesheet/${selectedEmployee.id}?${params.toString()}&format=${fileFormat}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          fileFormat === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Timesheet_${selectedEmployee.firstName}_${selectedEmployee.lastName}.${fileFormat === "pdf" ? "pdf" : "xlsx"
        }`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export report. Please try again.");
    }
  };

  // Generate dynamic days
  const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
  const days = Array.from({ length: dayCount }, (_, i) => addDays(startDate, i));

  const handlePrev = () => {
    if (viewMode === "monthly") {
      const newStart = sub(startDate, { months: 1 });
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = sub(startDate, { weeks: 1 });
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };

  const handleNext = () => {
    if (viewMode === "monthly") {
      const newStart = addMonths(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = addWeeks(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };

  const filteredWorklogs = (() => {
    if (!startDate && !endDate) return worklogs;

    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;

    if (from && to && to < from) {
      if (!invalidDateToastShown.current) {
        toast.error("End date must be greater than or equal to Start date.");
        invalidDateToastShown.current = true;
      }
      return worklogs;
    } else {
      invalidDateToastShown.current = false;
    }

    return worklogs.filter((log) => {
      const logDate = new Date(log.date);

      const inStart = from ? logDate >= from : true;
      const inEnd = to ? logDate <= to : true;

      if (!(inStart && inEnd)) return false;

      if (billableFilter === "all") return true;

      return log.billingType === billableFilter;
    });
  })();


  const getHours = (taskId, day, billableType) =>
    filteredWorklogs
      .filter(
        (log) =>
          log.taskId === taskId &&
          isSameDay(new Date(log.date), day) &&
          (!billableType || log.billingType === billableType)
      )
      .reduce((sum, log) => sum + log.hours, 0);

  const totalHours = filteredWorklogs.reduce((sum, log) => sum + log.hours, 0);
  const totalBillable = filteredWorklogs
    .filter((log) => log.billingType === "billable")
    .reduce((sum, log) => sum + log.hours, 0);
  const totalNonBillable = totalHours - totalBillable;

  const totalFilteredHours = filteredWorklogs.reduce((sum, log) => sum + log.hours, 0);
  const totalFilteredBillable = filteredWorklogs
    .filter((log) => log.billingType === "billable")
    .reduce((sum, log) => sum + log.hours, 0);
  const totalFilteredNonBillable = filteredWorklogs
    .filter((log) => log.billingType === "non-billable")
    .reduce((sum, log) => sum + log.hours, 0);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <div className="spinner-border text-primary"></div>
        <span className="ms-2 fs-5">Loading timesheet...</span>
      </div>
    );

  return (

    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <ManagerNavbar />
      </div>
      <div className="container-fluid mt-4" style={{ paddingTop: "110px" }}>
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-white rounded shadow-sm border gap-3">
          {/* Employee Selector */}
          <div className="d-flex align-items-center gap-2">
            <FaUsers className="text-primary fs-4" />
            <select
              className="form-select form-select-sm"
              style={{ minWidth: "200px" }}
              value={selectedEmployee?.id || ""}
              onChange={(e) => {
                const emp = employees.find((emp) => emp.id === parseInt(e.target.value));
                setSelectedEmployee(emp || null);
              }}
            >
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName} (ID: {emp.id})
                </option>
              ))}
            </select>
          </div>

          {/* Controls */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* <button
                        className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                        onClick={() => navigate("/managerdashboard")}
                    >
                        <FaHome /> Home
                    </button> */}
            <button
              className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
              onClick={() => navigate(`/teamtimesheetgrid/${managerId}`)}
            >
              <FaUsers /> All Team
            </button>

            <button
              className={`btn btn-sm ${viewMode === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setViewMode("monthly");
                setStartDate(startOfMonth(startDate));
                setEndDate(endOfMonth(startDate));
              }}
            >
              Monthly
            </button>
            <button
              className={`btn btn-sm ${viewMode === "weekly" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                const today = new Date(); // Get today's date
                setViewMode("weekly");
                setStartDate(startOfWeek(today, { weekStartsOn: 1 })); // Monday as start of week
                setEndDate(endOfWeek(today, { weekStartsOn: 1 }));     // Sunday as end of week
              }}
            >
              Weekly
            </button>

            <div className="btn-group btn-group-sm">
              <button
                className={`btn btn-sm ${billableFilter === "all" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("all")}
              >
                All
              </button>
              <button
                className={`btn btn-sm ${billableFilter === "billable" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("billable")}
              >
                Billable
              </button>
              <button
                className={`btn btn-sm ${billableFilter === "non-billable" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("non-billable")}
              >
                Non-Billable
              </button>
            </div>

            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-primary" onClick={handlePrev}>
                <FaChevronLeft />
              </button>
              <button className="btn btn-outline-primary" onClick={handleNext}>
                <FaChevronRight />
              </button>
            </div>
            {/* Date Range */}
            <div className="d-flex gap-1 align-items-center">
              <input
                type="date"
                value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
                onChange={(e) =>
                  setStartDate(e.target.value ? new Date(e.target.value) : null)
                }
                className="form-control form-control-sm"
                style={{ width: "110px" }}
              />

              <span className="text-muted">to</span>

              <input
                type="date"
                value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                onChange={(e) => {
                  const selected = e.target.value ? new Date(e.target.value) : null;

                  if (selected && startDate && selected < startDate) {
                    const today = new Date();
                    setEndDate(today);
                    toast.error(
                      "End date cannot be earlier than Start date. Reset to today."
                    );
                  } else {
                    setEndDate(selected);
                  }
                }}
                className="form-control form-control-sm"
                style={{ width: "110px" }}
              />
            </div>

            {/* Export Buttons */}
            <div className="btn-group btn-group-sm ms-2">
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleExport("pdf")}
              >
                Export PDF
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleExport("excel")}
              >
                Export Excel
              </button>
            </div>

          </div>
        </div>

        {/* Compact Summary Cards */}
        <div className="row g-2 mb-3 mt-2">
          {[
            { title: "Total Hours", value: totalFilteredHours, bg: "primary", icon: "bi-hourglass-split" },
            { title: "Billable", value: totalFilteredBillable, bg: "success", icon: "bi-cash-coin" },
            { title: "Non-Billable", value: totalFilteredNonBillable, bg: "danger", icon: "bi-slash-circle" },
          ].map((card, idx) => (
            <div key={idx} className="col-12 col-sm-4 col-md-2">
              <div className="card shadow-sm border-0 h-100 p-2">
                <div className="d-flex align-items-center gap-2">
                  {/* Icon */}
                  <div
                    className={`rounded-circle bg-${card.bg} bg-opacity-10 text-${card.bg} d-flex align-items-center justify-content-center`}
                    style={{ width: "35px", height: "35px" }}
                  >
                    <i className={`bi ${card.icon} fs-6`}></i>
                  </div>

                  {/* Text */}
                  <div className="flex-grow-1">
                    <h6 className="mb-0 fw-semibold" style={{ fontSize: "0.85rem" }}>
                      {card.title}
                    </h6>
                    <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {card.value} hrs
                    </small>
                  </div>

                  {/* Value */}
                  <h6 className={`mb-0 text-${card.bg} fw-bold`} style={{ fontSize: "0.9rem" }}>
                    {card.value}
                  </h6>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Timesheet Grid */}
        <div className="shadow-sm rounded border mt-3" style={{ overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "180px 90px 1fr", alignItems: "stretch" }}>

            {/* Sticky Left Column: Task */}
            <div style={{ position: "sticky", left: 0, zIndex: 30, background: "white" }}>
              <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed", width: "180px", marginBottom: 0 }}>
                <thead className="table-secondary sticky-top">
                  <tr>
                    <th className="bg-secondary text-white text-start px-3" style={{ minWidth: "180px", height: "65px", verticalAlign: "middle" }}>
                      Task
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length ? (
                    tasks.map((task, i) => (
                      <tr key={i}>
                        <td className="fw-normal text-start bg-light px-2">{task.title || `Task ${task.id}`}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="text-muted text-center py-4">No tasks</td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="fw-semibold table">
                  <tr className="table">
                    <td className="text-start px-3">Billable Total</td>
                  </tr>
                  <tr className="table">
                    <td className="text-start px-3">Non-Billable Total</td>
                  </tr>
                  <tr className="table">
                    <td className="text-start px-3">Daily Total</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Sticky Second Column: Total */}
            <div style={{ position: "sticky", left: 180, zIndex: 25, background: "white" }}>
              <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed", width: "90px", marginBottom: 0 }}>
                <thead className="table-primary sticky-top">
                  <tr>
                    <th className="bg-secondary text-white" style={{ minWidth: "90px", height: "65px", verticalAlign: "middle" }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length ? (
                    tasks.map((task, i) => {
                      const rowTotal = days.reduce((sum, day) => sum + getHours(task.id, day), 0);
                      return (
                        <tr key={i}>
                          <td className="fw-semibold">{rowTotal || "0"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="text-muted">0</td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="fw-semibold">
                  <tr className="table">
                    <td className="fw-semibold text-dark">{totalBillable}</td>
                  </tr>
                  <tr className="table">
                    <td className="fw-semibold text-dark">{totalNonBillable}</td>
                  </tr>
                  <tr className="table">
                    <td className="fw-semibold text-dark">{totalHours}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Scrollable Middle Section: Days */}
            <div style={{ overflowX: "auto" }}>
              <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed", minWidth: days.length * 70, marginBottom: 0 }}>
                <thead className="table-secondary sticky-top">
                  <tr>
                    {days.map((day, i) => {
                      const formatted = format(day, "yyyy-MM-dd");
                      const isHoliday = holidayDates.includes(formatted);
                      const holidayName = holidayMap[formatted];

                      return (
                        <th
                          key={i}
                          style={{
                            minWidth: "70px",
                            backgroundColor: isLeaveDay(day)
                              ? "#ffe6e6"          // 💚 light green for leave
                              : isHolidayColumn(day)
                                ? "#d4f8d4" // public holiday (from database)
                                : isWeeklyHoliday(day)
                                  ? "#f7f7f7" // weekly holiday (fetched from backend)
                                  : "",
                          }}
                          title={
                            isHolidayColumn(day)
                              ? holidayMap[format(day, "yyyy-MM-dd")]
                              : isWeeklyHoliday(day)
                                ? "Weekly Holiday"
                                : ""
                          }
                        >
                          {format(day, "d")} <br />
                          <small
                            className={isLeaveDay(day)
                                ? "text-danger fw-semibold"
                              :isHolidayColumn(day)
                                ? "text-secondary fw-semibold"
                                : isWeeklyHoliday(day)
                                  ? "text-secondary fw-semibold"
                                  : "text-muted"
                            }
                          >
                            {format(day, "EEE")}
                          </small>
                        </th>

                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tasks.length ? (
                    tasks.map((task, i) => (
                      <tr key={i}>
                        {days.map((day, j) => {
                          const logs = filteredWorklogs.filter(
                            (log) => log.taskId === task.id && isSameDay(new Date(log.date), day)
                          );

                          const billable = logs.filter((log) => log.billingType === "billable")
                            .reduce((sum, log) => sum + log.hours, 0);

                          const nonBillable = logs.filter((log) => log.billingType === "non-billable")
                            .reduce((sum, log) => sum + log.hours, 0);

                          const descriptions = logs
                            .map((log) => `${log.description || "No description"} (${log.hours}h)`)
                            .join("\n");

                          return (
                            <td
                              key={j}
                              className="text-center align-middle"
                              style={{
                                backgroundColor: isLeaveDay(day)
                                  ? "#fff0f0"          // 💚 light green for leave
                                  : isHolidayColumn(day)
                                    ? "#d4f8d4" // Public holiday (light red)
                                    : isWeeklyHoliday(day)
                                      ? "#f7f7f7" // Weekly holiday (light gray)
                                      : "",
                                cursor: "pointer", // For clickable feedback
                              }}
                              title={
                                isHolidayColumn(day)
                                  ? holidayMap[format(day, "yyyy-MM-dd")]
                                  : isWeeklyHoliday(day)
                                    ? "Weekly Holiday"
                                    : ""
                              }
                            // onClick={() => handleCellClick(task, day)} // Handle edit / input
                            >
                              {billable > 0 && <span className="text-dark">{billable}</span>}
                              {nonBillable > 0 && <span className="text-dark">{nonBillable}</span>}
                              {billable + nonBillable === 0 && <span style={{ visibility: "hidden" }}>0</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={days.length} className="text-muted py-4">No tasks</td>
                    </tr>
                  )}
                </tbody>

                <tfoot className="fw-normal">

                  {/* Billable total row */}
                  <tr className="table">
                    {days.map((day, i) => {
                      const billableTotal = tasks.reduce(
                        (sum, task) => sum + getHours(task.id, day, "billable"),
                        0
                      );

                      const isHoliday = holidayDates.includes(format(day, "yyyy-MM-dd"));
                      const isWeekly = isWeeklyHoliday(day);
                      const isLeaveDay = leaveDates.includes(format(day, "yyyy-MM-dd"));

                      return (
                        <td
                          key={i}
                          style={{
                            backgroundColor: isLeaveDay
                              ? "#fff0f0"
                              : isHoliday
                                ? "#d4f8d4" // public holiday
                                : isWeekly
                                  ? "#f7f7f7" // weekly holiday
                                  : "",
                          }}
                          title={
                            isHoliday
                              ? holidayMap[format(day, "yyyy-MM-dd")]
                              : isWeekly
                                ? "Weekly Holiday"
                                : ""
                          }
                        >
                          {billableTotal ? (
                            <span className="fw-semibold text-dark">{billableTotal}</span>
                          ) : (
                            <span style={{ visibility: "hidden" }}>0</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Non-billable total row */}
                  <tr className="table">
                    {days.map((day, i) => {
                      const nonBillableTotal = tasks.reduce(
                        (sum, task) => sum + getHours(task.id, day, "non-billable"),
                        0
                      );

                      const isHoliday = holidayDates.includes(format(day, "yyyy-MM-dd"));
                      const isWeekly = isWeeklyHoliday(day);
                      const isLeaveDay = leaveDates.includes(format(day, "yyyy-MM-dd"));
                      return (
                        <td
                          key={i}
                          style={{
                            backgroundColor: isLeaveDay
                              ? "#fff0f0"
                              : isHoliday
                                ? "#d4f8d4"
                                : isWeekly
                                  ? "#f7f7f7"
                                  : "",
                          }}
                          title={
                            isHoliday
                              ? holidayMap[format(day, "yyyy-MM-dd")]
                              : isWeekly
                                ? "Weekly Holiday"
                                : ""
                          }
                        >
                          {nonBillableTotal ? (
                            <span className="fw-semibold text-dark">{nonBillableTotal}</span>
                          ) : (
                            <span style={{ visibility: "hidden" }}>0</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Column total row */}
                  <tr className="table">
                    {days.map((day, i) => {
                      const colTotal = tasks.reduce(
                        (sum, task) => sum + getHours(task.id, day),
                        0
                      );

                      const isHoliday = holidayDates.includes(format(day, "yyyy-MM-dd"));
                      const isWeekly = isWeeklyHoliday(day);
                      const isLeaveDay = leaveDates.includes(format(day, "yyyy-MM-dd"));

                      return (
                        <td
                          key={i}
                          style={{
                            backgroundColor: isLeaveDay
                              ? "#fff0f0"
                              : isHoliday
                                ? "#d4f8d4"
                                : isWeekly
                                  ? "#f7f7f7"
                                  : "",
                          }}
                          title={
                            isHoliday
                              ? holidayMap[format(day, "yyyy-MM-dd")]
                              : isWeekly
                                ? "Weekly Holiday"
                                : ""
                          }
                        >
                          {colTotal ? (
                            <span className="fw-semibold text-dark">{colTotal}</span>
                          ) : (
                            <span style={{ visibility: "hidden" }}>0</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManagerTimesheetGrid;
